import { Service } from "@/utils/types";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import placeholderImage from "@/assets/img/comingSoon.jpeg";
import { Search, X, ChevronLeft } from "lucide-react";
import { ServiceDetails } from "./ServiceDetails";
import { Button } from "@/components/ui/button";
import { SelectedService } from "./CreateAppointmentForm";

export default function ServiceSelection({
  onClose,
  branch,
  setSelectedService,
  setShowServiceSelection,
}: {
  onClose: () => void;
  branch: number | null;
  setShowServiceSelection: (show: boolean) => void;
  setSelectedService: (service: SelectedService) => void;
}) {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchString, setSearchString] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [showServiceDetails, setShowServiceDetails] = useState<Service | null>(
    null,
  );

  const fetchServices = async (query = "") => {
    setLoading(true);
    if (branch) {
      try {
        const url = `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/product/?front_view=true&product_type=Services&branch=${branch}${query ? `&menu_display_name=${query}` : ""}`;
        const servicesRes = await axios.get(url);
        console.log(servicesRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchServices();
  }, [branch]);

  // Handle debounced search
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set a new timeout
    searchTimeout.current = setTimeout(() => {
      fetchServices(searchString);
    }, 300);

    // Cleanup function
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchString]);

  // Sort services alphabetically by name
  const sortedServices = [...services].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (showServiceDetails) {
    return (
      <ServiceDetails
        service={showServiceDetails}
        onClose={() => setShowServiceDetails(null)}
        onSubmit={() => {
          setShowServiceDetails(null);
          setShowServiceSelection(false);
        }}
        setSelectedService={setSelectedService}
      />
    );
  }

  return (
    <div className="container mx-auto md:p-6 p-2">
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-2">
        <h2 className="text-2xl font-bold text-gray-800">Select a Service</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search services..."
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              className="rounded-md border border-gray-300 py-1.5 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <Button
            variant="outline"
            onClick={onClose}
            className="flex items-center gap-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            <span>Back</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-red-500"></div>
        </div>
      ) : sortedServices.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-gray-500">
          <p className="text-xl">No services found</p>
          <p className="mt-2">Try adjusting your search criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {sortedServices.map((service) => (
            <div
              key={service.id}
              className="flex cursor-pointer flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md transition-all hover:shadow-lg"
              onClick={() => {
                setShowServiceDetails(service);
              }}
            >
              <div className="relative h-48 w-full overflow-hidden bg-gray-200">
                {service.image ? (
                  <img
                    src={service.image}
                    alt={service.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <img
                    src={placeholderImage}
                    alt={service.name}
                    className="h-full w-full object-cover"
                  />
                )}
              </div>

              <div className="flex flex-1 flex-col p-4">
                <h3 className="mb-2 text-xl font-semibold text-gray-800">
                  {service.menu_display_name}
                </h3>
                <p className="mb-4 line-clamp-3 flex-1 text-sm text-gray-600">
                  {service.description}
                </p>

                <div className="mt-auto flex items-center justify-between">
                  <span className="text-lg font-bold">
                    {parseFloat(service.unit_price).toFixed(2) === "0.00"
                      ? "Priced by Add-ons"
                      : `$${parseFloat(service.unit_price).toFixed(2)}`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
